// @vendors
const { Promise } = require('es6-promise');

// @utils
const apiURLBuilder = require('core/apiURLBuilder');
const { APIGet, APIPost } = require('utilities/APIRequestHelper');
const SanitizeHelper = require('utilities/sanitizeHelper');

// @constants
const actionTypes = require('constants/actionTypes');
const {
    TYPE_STOCKS,
    TYPE_PLANS,
    TYPE_WARRANTS,
    TYPE_FUNDS,
    TYPE_ETFS,
    FULL_PLANS_UNIVERSE_ID,
    SPAIN_PLANS_UNIVERSE_ID,
    FUNDS_ABLE_TO_BE_HIRED_FILTER,
    PLANS_ABLE_TO_BE_HIRED_FILTER,
    BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE,
    BROKER_INVESTMENT_FUNDS_PRODUCT_URL_PARAMETER,
    BROKER_PLANS_PRODUCT_URL_PARAMETER,
    BROKER_ADVANCED_SEARCH_URL_FUNDS_SECURITY_DATA_POINTS,
    BROKER_ADVANCED_SEARCH_URL_PLANS_SECURITY_DATA_POINTS,
    BROKER_ADVANCED_SEARCH_ETFS_REQUEST_IDENTIFIER,
    BROKER_ADVANCED_SEARCH_STOCKS_REQUEST_IDENTIFIER,
    BROKER_ADVANCED_SEARCH_FUND_UNIVERSE_ID,
    BROKER_ADVANCED_SEARCH_FUND_EXTERNAL_SEARCH_UNIVERSE_ID,
    BROKER_ADVANCED_SEARCH_SLIDER_MAX,
    BROKER_ADVANCED_SEARCH_SLIDER_MIN,
    BROKER_INTERNAL_TRANSFER_FUND_NO_RESULT_ERROR,
    BROKER_INTERNAL_TRANSFER_FUND_TOTAL_RESULT_ZERO
} = require('constants/index');

const productsUrls = {};
productsUrls[TYPE_FUNDS] = BROKER_INVESTMENT_FUNDS_PRODUCT_URL_PARAMETER;
productsUrls[TYPE_PLANS] = BROKER_PLANS_PRODUCT_URL_PARAMETER;

const getResultsFailure = (response, productType) => {
    return {
        type: actionTypes.BROKER_GET_SEARCH_RESULTS_LIST_FAILURE,
        payload: {
            data: response,
            productType
        }
    };
};

const getResultsSuccess = (response, productType) => {
    return {
        type: actionTypes.BROKER_GET_SEARCH_RESULTS_LIST_SUCCESS,
        payload: {
            data: response,
            productType
        }
    };
};

const getResultsFetching = () => {
    return {
        type: actionTypes.BROKER_GET_SEARCH_RESULTS_LIST_FETCHING,
        payload: {
            isFetching: true
        }
    };
};

const constructRiskString = selectedValues => {
    const lowRiskButton = selectedValues.getIn(['risk', 'low']);
    const mediumRiskButton = selectedValues.getIn(['risk', 'medium']);
    const highRiskButton = selectedValues.getIn(['risk', 'high']);
    let string = 'IN';
    if (lowRiskButton) {
        string = string + ':1:2';
    }
    if (mediumRiskButton) {
        string = string + ':3:4';
    }
    if (highRiskButton) {
        string = string + ':5:6:7';
    }
    if (
        (!lowRiskButton && !mediumRiskButton && !highRiskButton) ||
        (lowRiskButton && mediumRiskButton && highRiskButton)
    ) {
        string = '';
    }
    return string;
};

//If all checkboxes are selected then we return an empty filter so no filtering is made
const checkStar = (curr, prev) => {
    if (curr.filter(star => star > 0).size < 5)
        return `${prev}Rating:IN:${curr.filter(star => star > 0).join(':') + '|'}`;
    else return `${prev}`;
};

//If all risks are selected then return empty filter
const checkRisk = (prev, risksSelected) => {
    if (risksSelected === '') return `${prev}`;
    else return `${prev}srri:${risksSelected + '|'}`;
};

const hasUserChangedDefaultProfitabilityRange = (immFilters) => (
    immFilters.get('maxProfitabilityRange') !== BROKER_ADVANCED_SEARCH_SLIDER_MAX ||
    immFilters.get('minProfitabilityRange') !== BROKER_ADVANCED_SEARCH_SLIDER_MIN
);

const getFundsResultsListRequest = (
    selectedValues,
    product,
    isExternalTraspassSearch,
    dispatch,
    isInternalTransferFund
) => {
    const prodUrl = productsUrls[product];
    const term = selectedValues.getIn(['term']);
    const risksSelected = constructRiskString(selectedValues);
    const universeId = isExternalTraspassSearch
        ? BROKER_ADVANCED_SEARCH_FUND_EXTERNAL_SEARCH_UNIVERSE_ID
        : BROKER_ADVANCED_SEARCH_FUND_UNIVERSE_ID;

    const userChangedDefaultProfitabilityRange = hasUserChangedDefaultProfitabilityRange(selectedValues);
    let pageSize;
    if (isExternalTraspassSearch) {
        pageSize = '300';
    } else if (isInternalTransferFund) {
        pageSize = '200';
    } else {
        pageSize = '500';
    }

    let initialDone = false;
    const requestString = selectedValues.reduce((prev, curr, currKey) => {
        if (curr === BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE) {
            return prev;
        } else if (!initialDone) {
            initialDone = true;
        }

        switch (currKey) {
           case 'promoter':
               return `${prev}AdministratorCompanyId:EQ:${curr+'|'}`;
           case 'category':
               return `${prev}CustomCategoryId:EQ:${curr+'|'}`;
           case 'currency':
               return `${prev}CurrencyId:EQ:${curr+'|'}`;
           case 'stars':
               return checkStar(curr, prev);
           case 'minProfitabilityRange':
                return userChangedDefaultProfitabilityRange
                    ? `${prev}ReturnM0:GT:${curr+'|'}`
                    : prev;
           case 'maxProfitabilityRange':
                return userChangedDefaultProfitabilityRange
                    ? `${prev}ReturnM0:LT:${curr+'|'}`
                    : prev;
           case 'risk':
               return checkRisk(prev, risksSelected);
           case 'traspassableFund':
                return `${prev}CustomTransferTaxExempt:EQ:${(curr === 'yes')+'|'}`;
           default:
               return prev;
       }
    }, '');

    return new Promise(() => {
        const requestData = {
            query: {
                pageSize,
                outputType: 'json',
                languageId: 'es-ES',
                universeIds: universeId,
                securityDataPoints: BROKER_ADVANCED_SEARCH_URL_FUNDS_SECURITY_DATA_POINTS,
                filters: requestString.substring(0, requestString.length) + (!isExternalTraspassSearch ? FUNDS_ABLE_TO_BE_HIRED_FILTER : ''),
                sortOrder: pageSize !== '50' && "Rating"
            }
        };

        if (term) {
            requestData.query.term = SanitizeHelper.encodeData(term);
        }

        dispatch(getResultsFetching());

        APIGet(
            dispatch,
            apiURLBuilder.getURL('brokerAdvancedSearchFundsPlansFilters', { type: prodUrl }),
            requestData
        ).then(
            response => {
                const availabilityReqList = !!response.rows
                    ? response.rows.map(fund => {
                        return {
                            currency: fund.Currency,
                            instrumentId: fund.ISIN,
                        }
                    })
                    : [];
                const catalogueRequestData = {
                    query: {
                        availabilityReqList: availabilityReqList,
                        mobileRequest: false,
                    },
                };
                APIPost({
                    dispatch,
                    endpoint: 'catalogueBrokerSearchAvailability',
                    url: apiURLBuilder.getURL('catalogueBrokerSearchAvailability'),
                    params: catalogueRequestData
                })
                    .then(catalogueAvailabilityResponse => {
                        const filteredData = {
                            ...response,
                            rows:
                                !!response.rows &&
                                !!catalogueAvailabilityResponse.availabilityRespList
                                    ? response.rows.filter(fund => {
                                        const filteredFund = catalogueAvailabilityResponse.availabilityRespList
                                            .find(
                                                availableFund =>
                                                    availableFund.instrumentId === fund.ISIN
                                            );
                                        return !!filteredFund && filteredFund.marketable && filteredFund.visible
                                    })
                                    : [],
                        };
                        if (!!filteredData.rows) {
                            dispatch(
                                getResultsFailure(
                                    {
                                        ...filteredData,
                                        body: {
                                            message: BROKER_INTERNAL_TRANSFER_FUND_NO_RESULT_ERROR,
                                        },
                                    },
                                    product
                                )
                            );
                        }
                        if (filteredData.total > 0 && filteredData.rows.length === 0) {
                            filteredData.total = BROKER_INTERNAL_TRANSFER_FUND_TOTAL_RESULT_ZERO;
                        }
                        dispatch(getResultsSuccess(filteredData, product));
                    })
                    .catch(() => {
                        dispatch(getResultsFailure(response, product));
                    });
            },
            response => {
                dispatch(getResultsFailure(response, product));
            }
        );
    });
};

const getPlansResultsListRequest = (selectedValues, product, isExternalPlansTransferOrigin, dispatch) => {
    const prodUrl = productsUrls[product];
    const term = selectedValues.getIn(['term']);
    const risksSelected = constructRiskString(selectedValues);
    const universeId = isExternalPlansTransferOrigin ? FULL_PLANS_UNIVERSE_ID: SPAIN_PLANS_UNIVERSE_ID;
    const userChangedDefaultProfitabilityRange = hasUserChangedDefaultProfitabilityRange(selectedValues);

    let initialDone = false;
    let requestString = selectedValues.reduce((prev, curr, currKey) => {
        if (curr === BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE) {
            return prev;
        } else if (!initialDone) {
            initialDone = true;
        }

        switch (currKey) {
            case 'promoter':
                return `${prev}AdministratorCompanyId:EQ:${curr + '|'}`;
            case 'category':
                return `${prev}CustomCategoryId:EQ:${curr + '|'}`;
            case 'currency':
                return `${prev}CurrencyId:EQ:${curr + '|'}`;
            case 'stars':
                return checkStar(curr, prev);
            case 'minProfitabilityRange':
                return userChangedDefaultProfitabilityRange
                    ? `${prev}ReturnM0:GT:${curr + '|'}`
                    : prev;
            case 'maxProfitabilityRange':
                return userChangedDefaultProfitabilityRange
                    ? `${prev}ReturnM0:LT:${curr + '|'}`
                    : prev;
            case 'risk':
                return checkRisk(prev, risksSelected);
            case 'traspassableFund':
                return `${prev}CustomTransferTaxExempt:EQ:${curr === 'yes' + '|'}`;
            default:
                return prev;
        }
    }, '');

    const preFilter = requestString.substring(0, requestString.length);
    const filters = isExternalPlansTransferOrigin ? preFilter : preFilter + PLANS_ABLE_TO_BE_HIRED_FILTER;

    return new Promise(() => {
        const requestData = {
            query: {
                pageSize: '5000',
                outputType: 'json',
                languageId: 'es-ES',
                universeIds: universeId,
                securityDataPoints: BROKER_ADVANCED_SEARCH_URL_PLANS_SECURITY_DATA_POINTS,
                filters: filters,
                sortOrder: "Rating"
            }
        };

        if (term) {
            requestData.query.term = SanitizeHelper.encodeData(term);
        }

        dispatch(getResultsFetching());

        APIGet(
            dispatch,
            apiURLBuilder.getURL('brokerAdvancedSearchFundsPlansFilters', { type: prodUrl }),
            requestData
        ).then(
            response => {
                dispatch(getResultsSuccess(response, product));
            },
            response => {
                dispatch(getResultsFailure(response, product));
            }
        );
    });
};

const getStocksResultsListRequest = (selectedValues, product, dispatch) => {
    const term = SanitizeHelper.encodeData(selectedValues.getIn(['term']));

    let exchangeId;
    if (selectedValues.getIn(['market']).complete) {
        exchangeId = selectedValues.getIn(['market']).complete.exchangeId;
    }

    return new Promise(() => {
        const requestData = {
            query: {
                pageNum: '0',
                pageSize: '0',
                searchBy: '3',
                searchMode: '3',
                category: BROKER_ADVANCED_SEARCH_STOCKS_REQUEST_IDENTIFIER,
                searchText: term
            }
        };

        if (exchangeId || exchangeId === 0) {
            requestData.query.exchangeId = exchangeId;
        }

        dispatch(getResultsFetching());

        APIGet(
            dispatch,
            apiURLBuilder.getURL('brokerAdvancedSearchMedysifSearch', { product: 'stock' }),
            requestData
        ).then(
            response => {
                dispatch(getResultsSuccess(response, product));
            },
            response => {
                dispatch(getResultsFailure(response, product));
            }
        );
    });
};

const constructEtfsPipesString = arr => {
    const arrString = arr.toString();
    const stringWithPipes = arrString.replace(/,/g, '|');
    return stringWithPipes;
};

const getETFsResultsListRequest = (selectedValues, product, dispatch) => {
    const term = selectedValues.getIn(['term']);
    const category = selectedValues.getIn(['category']);
    const issuer = selectedValues.getIn(['issuer']);
    const leverage = selectedValues.getIn(['leverage']);
    const complexity = selectedValues.getIn(['complexity']);
    const market = selectedValues.get('market');
    const valuesArray = [category, issuer, leverage, complexity, market];
    const cleaned = valuesArray.filter(
        val => val !== BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE && val
    );
    const pipesString = constructEtfsPipesString(cleaned);

    const requestData = {
        query: {
            pageNum: '0',
            pageSize: '0',
            category: BROKER_ADVANCED_SEARCH_ETFS_REQUEST_IDENTIFIER
        }
    };
    if (term) {
        requestData.query.searchText = SanitizeHelper.encodeData(term);
    }

    if (cleaned.length) {
        requestData.query.idClassification = pipesString;
    }

    dispatch(getResultsFetching());

    return APIGet(
        dispatch,
        apiURLBuilder.getURL('brokerAdvancedSearchETFsSearch'),
        requestData
    ).then(
        response => {
            dispatch(getResultsSuccess(response, product));
        },
        response => {
            dispatch(getResultsFailure(response, product));
        }
    );
};

const getWarrantsResultsListRequest = (selectedValues, product, dispatch) => {
    const type = selectedValues.getIn(['type']);
    const resultProduct = selectedValues.getIn(['product']);
    const issuer = selectedValues.getIn(['issuer']);
    const underlying = selectedValues.getIn(['underlying', 'value']);
    const underlyingExchangeId = selectedValues.getIn(['underlying', 'ids', 'exchangeId']);
    const underlyingSymbol = selectedValues.getIn(['underlying', 'ids', 'spcSymbol']);

    const expiration = selectedValues.getIn(['expiration'])
        ? selectedValues.getIn(['expiration']).format('DD/MM/YYYY')
        : '';

    const requestData = {
        query: {
            pageNum: '0',
            pageSize: '0',
            endMadurityDate: expiration
        }
    };

    if (type !== BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE) {
        requestData.query.typeId = type;
    }
    if (issuer !== BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE) {
        requestData.query.issuerId = issuer;
    }
    if (underlying !== BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE) {
        requestData.query.underlyingExchangeId = underlyingExchangeId;
        requestData.query.underlyingSymbol = underlyingSymbol;
    }
    if (resultProduct !== BROKER_ADVANCED_SEARCH_DEFAULT_ALL_VALUE) {
        requestData.query.productId = resultProduct;
    }

    dispatch(getResultsFetching());

    return APIGet(
        dispatch,
        apiURLBuilder.getURL('brokerAdvancedSearchMedysifSearch', { product: 'warrants' }),
        requestData
    ).then(
        response => {
            dispatch(getResultsSuccess(response, product));
        },
        response => {
            dispatch(getResultsFailure(response, product));
        }
    );
};

const getResultsListRequest = (selectedValues, product, isExternalTraspassSearch, isExternalPlansTransferOrigin, dispatch, isInternalTransferFund) => {
    if (product === TYPE_STOCKS) {
        return getStocksResultsListRequest(selectedValues, product, dispatch);
    }

    if (product === TYPE_WARRANTS) {
        return getWarrantsResultsListRequest(selectedValues, product, dispatch);
    }

    if (product === TYPE_ETFS) {
        return getETFsResultsListRequest(selectedValues, product, dispatch);
    }

    if (product === TYPE_FUNDS) {
        return getFundsResultsListRequest(
            selectedValues,
            product,
            isExternalTraspassSearch,
            dispatch,
            isInternalTransferFund
        );
    }

    if (product === TYPE_PLANS) {
        return getPlansResultsListRequest(selectedValues, product, isExternalPlansTransferOrigin, dispatch);
    }
};

const getResultsList = (values, activeProduct, isExternalTraspassSearch, isExternalPlansTransferOrigin, isInternalTransferFund) => dispatch =>
    getResultsListRequest(values, activeProduct, isExternalTraspassSearch, isExternalPlansTransferOrigin, dispatch, isInternalTransferFund);

module.exports = getResultsList;
